import {assert} from 'Asserts';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartNodeConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
  ProcessFlowchartSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  findAllUpstreamMappableNodeIds,
  isMappableTargetNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';

import buildNetworkFlowchartFromStaticData, {
  buildPtdvIdToTaskSchemaIdFromNodes,
} from '../buildNetworkFlowchartConfigFromStaticData';
import {
  FlowchartStaticEdgeData,
  PersistedSchemaMapperConfigForExecutionSpec,
} from '../processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {ProcessTaskDefinitionsResult} from '../useAllSchemasById';

/**
 * ACM validation compares execution-node PTDV references against flowchart config specs, whose
 * task-definition-version client ids use the backend `ptdv:{api_id}` format.
 */
function normalizeTaskDefinitionVersionClientId(raw: string | null | undefined): string {
  assert(
    raw != null && raw !== '',
    'Expected task definition version client id for execution flowchart spec'
  );
  return raw.startsWith('ptdv:') ? raw : `ptdv:${raw}`;
}

export enum ExecutionFlowchartSpecVariant {
  ACM_CREATE = 'ACM_CREATE',
  PCM_UPDATE = 'PCM_UPDATE',
}

interface BuildExecutionFlowchartSpecFromDraftStateArgs {
  /** The current ACM/PCM draft to persist. */
  draftState: FlowchartConfigWithMetadata;
  /**
   * Used as the spec's top-level clientId. The backend uses this as an internal identifier when
   * constructing the new execution flowchart (it does not need to match an existing flowchart id).
   *
   * For PCM, pass the loaded flowchart config id (or the execution flowchart id). For ACM, pass a
   * freshly generated client id that will also be attached to each task via
   * `task.executionFlowchartClientId`.
   */
  flowchartClientId: string;
  /**
   * Persisted schema mapper configs from the current flowchart config version (resolved API ids,
   * not synthetic client ids). Used to translate drawn control-flow edges back to references to
   * persisted `ProcessFlowchartSchemaMapperConfig` rows so the resulting execution flowchart can
   * be persisted by `build_flowchart_and_client_id_to_component_maps`, which looks each id up via
   * `ProcessFlowchartSchemaMapperConfig.bulk_load_and_check_auth`.
   */
  persistedSchemaMapperConfigs: ReadonlyArray<PersistedSchemaMapperConfigForExecutionSpec>;
  /**
   * Task definitions known to the editor; used to resolve PTDVs that are referenced only by
   * optional transitions (no node on the canvas). Defaults to no additional definitions if
   * omitted — sufficient for ACM/PCM edits that only touch structural nodes/edges.
   */
  taskDefinitions?: ProcessTaskDefinitionsResult;
  /**
   * Selects whether each task/root node emits its `taskDefinitionVersionClientId` in the backend
   * `ptdv:{api_id}` shape (ACM createProcess) or leaves it null (PCM updateProcess).
   *
   * ACM createProcess preloads a `client_id_to_task_definition_version` map from the flowchart
   * config spec where every PTDV is keyed by `ptdv:{api_id}`, so node specs must match that shape
   * or backend validation fails with mapper-vs-node id mismatches. PCM updateProcess does not
   * preload that map; if `taskDefinitionVersionClientId` is omitted the backend resolves the task
   * version from the node's task schema instead, which is the only path that works for PCM.
   */
  specVariant?: ExecutionFlowchartSpecVariant;
}

function buildInboundSchemaMappersForTarget(args: {
  target: TaskSchemaStaticNodeData;
  nodesStaticData: ReadonlyArray<StaticFlowchartNodeData>;
  edgesStaticData: ReadonlyArray<FlowchartStaticEdgeData>;
  persistedSchemaMapperConfigs: ReadonlyArray<PersistedSchemaMapperConfigForExecutionSpec>;
}): Array<ProcessFlowchartSchemaMapperSpec> {
  const {target, nodesStaticData, edgesStaticData, persistedSchemaMapperConfigs} = args;
  const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(
    {
      nodes: nodesStaticData.map((node) => ({id: node.id, data: node})),
      edges: [...edgesStaticData],
    },
    target.id
  );
  const upstreamMappableNodes = upstreamMappableNodeIds
    .map((id) => nodesStaticData.find((node) => node.id === id))
    .filter((node): node is TaskSchemaStaticNodeData => node != null && isStaticTaskSchemaNodeData(node));
  const targetPtdvId = target.taskDefinitionVersionSpec.clientId;
  const mapsToType =
    target.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;

  const mappers: Array<ProcessFlowchartSchemaMapperSpec> = [];
  const seenPersistedIds = new Set<string>();
  for (const upstreamNode of upstreamMappableNodes) {
    const sourcePtdvId = upstreamNode.taskDefinitionVersionSpec.clientId;
    const matched = persistedSchemaMapperConfigs.find(
      (config) =>
        config.mapsToType === mapsToType &&
        config.targetPtdvId === targetPtdvId &&
        config.sourcePtdvIds.includes(sourcePtdvId)
    );
    if (matched == null || seenPersistedIds.has(matched.id)) {
      continue;
    }
    seenPersistedIds.add(matched.id);

    // Capture every drawn upstream node whose PTDV is part of this mapper's sources, so merging
    // transitions (multiple sources → one target) emit a single mapper carrying all dependencies.
    const dependencyNodes = upstreamMappableNodes.filter((node) =>
      matched.sourcePtdvIds.includes(node.taskDefinitionVersionSpec.clientId)
    );
    if (dependencyNodes.length === 0) {
      continue;
    }
    mappers.push({
      toNodeConfigClientId: target.id,
      schemaMapperConfigClientId: matched.id,
      schemaMapperDependencies: dependencyNodes.map((node) => ({fromNodeConfigClientId: node.id})),
    });
  }

  return mappers;
}

/**
 * Strips template-only fields and rewrites mapper references to persisted schema-mapper ids for
 * execution-flowchart endpoints.
 */
function adaptNodeForExecutionFlowchartSpec(
  node: ProcessFlowchartNodeConfigSpec,
  inboundSchemaMappersByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>,
  specVariant: ExecutionFlowchartSpecVariant
): ProcessFlowchartNodeConfigSpec {
  const {nodeDetails} = node;
  const inboundSchemaMappers = inboundSchemaMappersByNodeId[node.clientId] ?? [];

  switch (nodeDetails.type) {
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      return {
        clientId: node.clientId,
        nodeDetails: {
          type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
          taskSchemaId: nodeDetails.taskSchemaId,
          taskDefinitionVersionClientId:
            specVariant === ExecutionFlowchartSpecVariant.ACM_CREATE
              ? normalizeTaskDefinitionVersionClientId(nodeDetails.taskDefinitionVersionClientId)
              : null,
          extension: null,
        },
        inboundSchemaMappers,
      };
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return {
        clientId: node.clientId,
        nodeDetails: {
          type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
          outputSchemaId: nodeDetails.outputSchemaId,
        },
        inboundSchemaMappers,
      };
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return {
        clientId: node.clientId,
        nodeDetails: {
          type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          taskSchemaId: nodeDetails.taskSchemaId,
          taskDefinitionVersionClientId:
            specVariant === ExecutionFlowchartSpecVariant.ACM_CREATE
              ? normalizeTaskDefinitionVersionClientId(nodeDetails.taskDefinitionVersionClientId)
              : null,
        },
      };
    default:
      return {
        clientId: node.clientId,
        nodeDetails,
      };
  }
}

/**
 * Converts an ACM/PCM execution-flowchart draft into the network {@link ProcessFlowchartSpec}
 * accepted by execution-flowchart endpoints (e.g. `ProcessManagementDAO.updateProcess` for PCM,
 * `ProcessManagementDAO.createProcess.flowcharts` for ACM).
 *
 * Steps:
 * 1. Reuse `buildNetworkFlowchartFromStaticData` to get baseline nodes/edges from the draft's
 *    static flowchart.
 * 2. Walk each mappable target (task/output) along its control-flow edges, find the
 *    upstream mappable nodes, and look up the persisted schema mapper config that matches the
 *    `(sources, target, mapsToType)` triple. That gives us the persisted API id to reference
 *    on the target's `inboundSchemaMappers`.
 * 3. Adapt each node for the execution spec: strip template-only fields and attach the resolved
 *    inbound mappers (empty arrays are fine; the backend just needs the key to exist on
 *    mappable nodes for `_ensure_node_fulfills_all_mappers`).
 *
 * The clientId returned on the spec is overridden with `flowchartClientId` so callers can pass
 * the right identifier for ACM vs PCM without leaking the flowchart-config id through to tasks.
 */
export function buildExecutionFlowchartSpecFromDraftState(
  args: BuildExecutionFlowchartSpecFromDraftStateArgs
): ProcessFlowchartSpec {
  const {
    draftState,
    flowchartClientId,
    taskDefinitions,
    persistedSchemaMapperConfigs,
    specVariant = ExecutionFlowchartSpecVariant.ACM_CREATE,
  } = args;
  const {staticFlowchart, metadata} = draftState;

  const networkConfig = buildNetworkFlowchartFromStaticData({
    staticFlowchart,
    options: {
      transitions: metadata.transitions,
      rootTaskSchema: metadata.rootTaskSchema,
      rootNodeStaticData: metadata.rootNodeStaticData,
      flowchartConfigId: flowchartClientId,
      options: metadata.options,
      ptdvIdToTaskSchemaId: buildPtdvIdToTaskSchemaIdFromNodes(
        staticFlowchart.nodesStaticData,
        taskDefinitions ?? []
      ),
    },
  });

  const templateFlowchart = networkConfig.templateFlowchart;
  assert(
    templateFlowchart != null,
    'Expected buildNetworkFlowchartFromStaticData to return a templateFlowchart'
  );

  const inboundSchemaMappersByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>> = {};
  for (const node of staticFlowchart.nodesStaticData) {
    if (!isMappableTargetNode({id: node.id, data: node}) || !isStaticTaskSchemaNodeData(node)) {
      continue;
    }
    const mappers = buildInboundSchemaMappersForTarget({
      target: node,
      nodesStaticData: staticFlowchart.nodesStaticData,
      edgesStaticData: staticFlowchart.edgesStaticData,
      persistedSchemaMapperConfigs,
    });
    if (mappers.length > 0) {
      inboundSchemaMappersByNodeId[node.id] = mappers;
    }
  }

  return {
    clientId: flowchartClientId,
    nodes: templateFlowchart.nodes.map((node) =>
      adaptNodeForExecutionFlowchartSpec(node, inboundSchemaMappersByNodeId, specVariant)
    ),
    edges: templateFlowchart.edges,
  };
}
